import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLazyGetMapPreviewQuery } from "@/pages/Private/redux/map/map.api";
import { runOrToast } from "@/utils/runOrToast";

import { IMapResponseItem } from "../../Map/interface";

/**
 * The results preview behind a Maps source's preview button in AddedSourceList. A refused preview
 * request used to escape as an unhandled rejection, so the button did nothing and said nothing
 * (PREC-4390); it now says why and leaves the preview closed.
 */
export const useSourcePreview = () => {
	const { t } = useTranslation();
	const [fetchPreview, { isLoading: isPreviewLoading }] = useLazyGetMapPreviewQuery();
	const [previewRows, setPreviewRows] = useState<IMapResponseItem[] | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);

	const openPreview = useCallback(
		async (source: { id: number }) => {
			await runOrToast(async () => {
				const results = await fetchPreview(source.id).unwrap();

				if (results.data) {
					setPreviewRows(results.data);
				}

				setIsPreviewOpen(true);
			}, t("errors.previewFailed"));
		},
		[fetchPreview, t]
	);

	const closePreview = useCallback(() => setIsPreviewOpen(false), []);

	return { isPreviewOpen, isPreviewLoading, previewRows, openPreview, closePreview };
};
